import { useState, type ReactNode } from "react"

import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { clsx } from "clsx"
import { useTranslation } from "react-i18next"

import {
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/battery-pricing-errors"
import type {
  BatteryPricingStatus,
  BatteryProposalDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryProposalFinancing from "~/api/hooks/useBatteryProposalFinancing"
import useBatteryProposalPricing from "~/api/hooks/useBatteryProposalPricing"
import useCalculateBatteryProposalPricing from "~/api/hooks/useCalculateBatteryProposalPricing"
import useCompleteBatteryInitialSetup from "~/api/hooks/useCompleteBatteryInitialSetup"
import useProposalConsumptionSelection from "~/api/hooks/useProposalConsumptionSelection"
import { useProposalLoanProductPartners } from "~/api/hooks/useProposalLoanProductPartners"
import { getAxiosErrorHowToFix, parseAxiosError } from "~/utils/parseAxiosError"

import { isBatteryProposalReadOnly } from "../battery-proposal-read-only"
import {
  isBatteryUtilitySaved,
  savedAnnualEnergyUse,
} from "./battery-initial-setup-progress"
import { batteryPricingSetupGaps } from "./battery-pricing-setup-gaps"
import { EquipmentForm } from "./EquipmentForm"
import { financePartnerLabel } from "./finance-partner-label"
import { SungageBrightStartPicker } from "./SungageBrightStartPicker"
import { UtilityForm } from "./UtilityForm"
import { SUNGAGE_BATTERY_FINANCE_TYPE } from "../sungage/sungage-battery-copy"

type SectionId = `equipment` | `pricing` | `utility`

const SECTION_LABELS: Record<SectionId, string> = {
  utility: `Utility & consumption`,
  equipment: `Finance Partner, Loan Program & equipment`,
  pricing: `Initial pricing`,
}

const SECTION_ORDER: readonly SectionId[] = [`utility`, `equipment`, `pricing`]

/** What a locked section is still waiting on, so the order is never a mystery. */
const LOCKED_HINTS: Record<SectionId, string | null> = {
  utility: null,
  equipment: `Save Utility & consumption first — financing and equipment depend on the Utility Company.`,
  pricing: `Save financing and equipment first — the first price is calculated from them.`,
}

const formatKwh = (value: number): string =>
  `${new Intl.NumberFormat(`en-US`, { maximumFractionDigits: 0 }).format(value)} kWh / year`

const Section = ({
  id,
  index,
  state,
  hint,
  summary,
  onEdit,
  children,
}: {
  id: SectionId
  index: number
  state: `done` | `locked` | `open`
  /** Why this section is still waiting; omitted once the flow has reached it. */
  hint?: string
  summary?: ReactNode
  onEdit?: () => void
  children?: ReactNode
}): JSX.Element => (
  <section
    data-testid={`battery-initial-setup-${id}`}
    data-state={state}
    aria-labelledby={`battery-initial-setup-${id}-heading`}
    className={clsx(
      `rounded-xl border p-5 sm:p-6`,
      state === `locked`
        ? `border-bp-border bg-bp-surface/40`
        : `border-bp-border-strong bg-bp-elevated`,
    )}
  >
    <div className="flex flex-wrap items-center gap-3">
      <span
        aria-hidden
        className={clsx(
          `flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[length:var(--step--2)] font-semibold`,
          state === `done`
            ? `bg-bp-success/20 text-bp-success`
            : state === `open`
              ? `bg-bp-accent text-bp-on-accent`
              : `bg-bp-glass text-bp-text-dim`,
        )}
      >
        {state === `done` ? <CheckCircleIcon className="h-5 w-5" /> : index}
      </span>
      <h2
        id={`battery-initial-setup-${id}-heading`}
        className={clsx(
          `font-display grow text-[length:var(--step-0)] font-semibold`,
          state === `locked` ? `text-bp-text-dim` : `text-bp-text`,
        )}
      >
        {SECTION_LABELS[id]}
      </h2>
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="shrink-0 rounded-md border border-bp-border-strong px-3 py-1.5 text-[length:var(--step--2)] font-semibold text-bp-text-soft transition-colors hover:border-bp-accent hover:text-bp-text"
        >
          Edit {SECTION_LABELS[id]}
        </button>
      ) : null}
    </div>
    {summary != null ? (
      <div className="mt-3 pl-10 text-[length:var(--step--1)] text-bp-text-soft">
        {summary}
      </div>
    ) : null}
    {state === `locked` && hint != null ? (
      <p className="mt-3 pl-10 text-[length:var(--step--2)] text-bp-text-dim">
        {hint}
      </p>
    ) : null}
    {children != null ? <div className="mt-5">{children}</div> : null}
  </section>
)

const FinishPricing = ({
  proposal,
}: {
  proposal: BatteryProposalDto
}): JSX.Element => {
  const proposalId = proposal.id
  const isSungage =
    proposal.financePlan?.financeType === SUNGAGE_BATTERY_FINANCE_TYPE
  const [notCurrent, setNotCurrent] = useState<BatteryPricingStatus | null>(null)
  const pricingQuery = useBatteryProposalPricing(proposalId)
  const financingQuery = useBatteryProposalFinancing(proposalId, {
    enabled: isSungage,
  })
  const calculatePricing = useCalculateBatteryProposalPricing()
  const completeSetup = useCompleteBatteryInitialSetup()
  const pricingCurrent = pricingQuery.data?.pricingStatus === `current`
  const pending = calculatePricing.isLoading || completeSetup.isLoading
  const failed =
    calculatePricing.isError || completeSetup.isError || notCurrent != null
  // Inputs this page cannot edit — Powur Project, property address, Requested
  // Fixed Margin — still gate pricing, so name them instead of letting the
  // seller discover them by pressing the final action.
  const blockingGaps = batteryPricingSetupGaps(proposal).filter(
    (gap) => gap.step == null,
  )
  const canEdit =
    !isBatteryProposalReadOnly(proposal) &&
    financingQuery.data?.currentApplication?.editLocked !== true &&
    financingQuery.data?.financingLocked !== true

  const calculate = (): void => {
    calculatePricing.mutate(
      { proposalId },
      {
        onSuccess: (priced) => {
          // The contract permits completion only after pricing reports
          // current. A 200 carrying any other status is a failed attempt, not
          // a reason to post a completion the server would refuse.
          if (priced.pricingStatus !== `current`) {
            setNotCurrent(priced.pricingStatus)
            return
          }

          setNotCurrent(null)
          if (!isSungage) {
            completeSetup.mutate({ proposalId })
          }
        },
      },
    )
  }

  const openProposal = (): void => {
    completeSetup.mutate({ proposalId })
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[length:var(--step--1)] text-bp-text-soft">
        {isSungage
          ? `Calculate the first price, then select a BrightStart Loan Product so monthly pricing can run. Adders and protection plans stay in the proposal itself.`
          : `Calculating the first price finishes setup and opens the proposal. Loan Product, adders, and protection plans stay in the proposal itself.`}
      </p>
      {blockingGaps.length > 0 ? (
        <div
          role="status"
          className="flex flex-col gap-1 text-[length:var(--step--2)] text-bp-text-soft"
        >
          <p>
            Pricing also needs project information this page cannot edit. Ask an
            administrator to fill in:
          </p>
          <ul className="list-disc pl-5">
            {blockingGaps.map((gap) => (
              <li key={gap.id}>{gap.label}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {calculatePricing.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {isPricingSupersededError(calculatePricing.error)
            ? PRICING_SUPERSEDED_MESSAGE
            : parseAxiosError(
                calculatePricing.error,
                `Unable to calculate pricing. Your saved setup is untouched.`,
              )}
        </p>
      ) : null}
      {notCurrent != null ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          Pricing came back {notCurrent.replace(/_/g, ` `)} rather than current,
          so setup was not finished. Your saved setup is untouched.
        </p>
      ) : null}
      {completeSetup.isError ? (
        <div role="alert" className="flex flex-col gap-1">
          <p className="text-[length:var(--step--2)] text-bp-danger">
            {parseAxiosError(
              completeSetup.error,
              `Pricing succeeded but setup could not be finished.`,
            )}
          </p>
          {/* The gateway names the sections it is still missing here. */}
          {getAxiosErrorHowToFix(completeSetup.error) != null ? (
            <p className="text-[length:var(--step--2)] text-bp-text-soft">
              {getAxiosErrorHowToFix(completeSetup.error)}
            </p>
          ) : null}
        </div>
      ) : null}
      {isSungage && pricingCurrent && financingQuery.isError ? (
        <p role="alert" className="text-[length:var(--step--2)] text-bp-danger">
          {parseAxiosError(
            financingQuery.error,
            `Unable to load BrightStart Loan Products.`,
          )}
        </p>
      ) : null}
      {isSungage && pricingCurrent && financingQuery.isLoading ? (
        <p
          role="status"
          className="text-[length:var(--step--1)] text-bp-text-soft"
        >
          Loading BrightStart Loan Products
        </p>
      ) : null}
      {isSungage && pricingCurrent && financingQuery.data != null ? (
        <SungageBrightStartPicker
          proposalId={proposalId}
          financing={financingQuery.data}
          canEdit={canEdit}
        />
      ) : null}
      {isSungage && pricingCurrent ? (
        <button
          type="button"
          disabled={pending}
          onClick={openProposal}
          className="self-start rounded-md bg-bp-accent px-5 py-2.5 text-[length:var(--step--1)] font-semibold text-bp-on-accent transition-colors hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {completeSetup.isLoading ? `Opening proposal` : `Open proposal`}
        </button>
      ) : (
        <button
          type="button"
          disabled={pending}
          onClick={calculate}
          className="self-start rounded-md bg-bp-accent px-5 py-2.5 text-[length:var(--step--1)] font-semibold text-bp-on-accent transition-colors hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {calculatePricing.isLoading
            ? `Calculating pricing`
            : completeSetup.isLoading
              ? `Opening proposal`
              : failed
                ? `Try again`
                : isSungage
                  ? `Calculate pricing`
                  : `Calculate pricing & open proposal`}
        </button>
      )}
    </div>
  )
}

/**
 * The required initial setup page for an incomplete Battery-Only Proposal.
 * Three ordered sections wrap the same Utility and Finance Partner & Equipment
 * forms the setup drawers use; the last one prices the system and asks the
 * server to record completion, which is what opens the normal proposal.
 */
export const BatteryInitialSetup = ({
  proposal,
}: {
  proposal: BatteryProposalDto
}): JSX.Element => {
  const { t } = useTranslation()
  const proposalId = proposal.id
  const consumptionQuery = useProposalConsumptionSelection(proposalId)
  const consumption = consumptionQuery.data
  const partnersQuery = useProposalLoanProductPartners(proposalId)
  const utilitySaved = isBatteryUtilitySaved({ proposal, consumption })
  // Until the selection read settles the page cannot tell a fresh proposal from
  // a resumed one. Opening Utility on that guess mounts its form, which puts a
  // throwaway Genability account write on every resumed load.
  const progressKnown = !consumptionQuery.isLoading
  /**
   * Financing and equipment need the seller's own save or Continue. Stored
   * equipment alone does not prove the seller reviewed it, and the server
   * cannot tell a carried-over selection from a reviewed one. Clones no longer
   * reach this page at all: they open setup-complete, and price from the
   * proposal itself once the seller's selections are final (VIS-554).
   */
  const [equipmentReviewed, setEquipmentReviewed] = useState(false)
  /** A reopened section; null follows the ordered flow. */
  const [reopened, setReopened] = useState<SectionId | null>(null)

  const nextSection: SectionId = !utilitySaved
    ? `utility`
    : !equipmentReviewed
      ? `equipment`
      : `pricing`
  const openSection = reopened ?? nextSection

  const stateOf = (id: SectionId): `done` | `locked` | `open` => {
    if (!progressKnown) {
      return `locked`
    }
    if (id === openSection) {
      return `open`
    }
    return SECTION_ORDER.indexOf(id) < SECTION_ORDER.indexOf(nextSection)
      ? `done`
      : `locked`
  }

  /**
   * A section is only "waiting" when the flow has not reached it. A reopened
   * earlier section leaves later ones collapsed without making them unreached,
   * and telling a finished section it still needs saving would be a lie.
   */
  const hintFor = (id: SectionId): string | undefined =>
    SECTION_ORDER.indexOf(id) > SECTION_ORDER.indexOf(nextSection)
      ? (LOCKED_HINTS[id] ?? undefined)
      : undefined

  const onUtilityConfirmed = (): void => {
    // Corrections upstream re-open the dependent section: eligibility and
    // pricing must run on the selections the seller ends up with.
    setEquipmentReviewed(false)
    setReopened(null)
  }

  const onEquipmentReviewed = (): void => {
    setEquipmentReviewed(true)
    setReopened(null)
  }

  const savedAnnual = savedAnnualEnergyUse(consumption)
  const partnerLabel = ((): string | null => {
    const financeType = proposal.financePlan?.financeType
    if (financeType == null) {
      return null
    }
    const partner = (partnersQuery.data ?? []).find(
      (option) => option.financeType === financeType,
    )
    if (partner == null) {
      return financeType
    }
    return financePartnerLabel(partner, (key) => t(key as never) as string)
  })()
  const equipmentLabel = proposal.equipment
    ? [proposal.equipment.manufacturer, proposal.equipment.name]
        .filter(Boolean)
        .join(` `)
    : null

  return (
    <div
      data-testid="battery-initial-setup"
      className="flex flex-col gap-5 px-4 py-8 sm:px-8 sm:py-12"
    >
      <header className="flex flex-col gap-2">
        <p className="text-[length:var(--step--2)] font-semibold tracking-wide text-bp-text-dim uppercase">
          Battery-Only Proposal
        </p>
        <h1 className="font-display text-[length:var(--step-2)] font-semibold text-bp-text">
          Finish initial setup
        </h1>
        <p className="max-w-2xl text-[length:var(--step--1)] text-bp-text-soft">
          Three steps stand between this proposal and its first price. Each one
          saves as you go, so you can leave and pick it up here.
        </p>
      </header>

      <Section
        id="utility"
        index={1}
        state={stateOf(`utility`)}
        summary={
          stateOf(`utility`) === `done` ? (
            <p>
              {[
                proposal.tariff?.utilityCompanyName,
                proposal.tariff?.tariffName ?? proposal.tariff?.tariffCode,
                savedAnnual != null ? formatKwh(savedAnnual) : null,
              ]
                .filter(Boolean)
                .join(` · `)}
            </p>
          ) : undefined
        }
        onEdit={
          stateOf(`utility`) === `done`
            ? () => setReopened(`utility`)
            : undefined
        }
      >
        {stateOf(`utility`) === `open` ? (
          <UtilityForm
            key={proposalId}
            proposalId={proposalId}
            onConfirmed={onUtilityConfirmed}
          />
        ) : null}
      </Section>

      <Section
        id="equipment"
        index={2}
        state={stateOf(`equipment`)}
        hint={hintFor(`equipment`)}
        summary={
          stateOf(`equipment`) === `done` ? (
            <p>
              {[
                partnerLabel,
                proposal.selectedLoanProgram?.name,
                equipmentLabel != null && proposal.equipment != null
                  ? `${equipmentLabel} × ${proposal.equipment.quantity}`
                  : null,
              ]
                .filter(Boolean)
                .join(` · `)}
            </p>
          ) : undefined
        }
        onEdit={
          stateOf(`equipment`) === `done`
            ? () => setReopened(`equipment`)
            : undefined
        }
      >
        {stateOf(`equipment`) === `open` ? (
          <EquipmentForm
            proposalId={proposalId}
            onSaved={onEquipmentReviewed}
            onContinueUnchanged={onEquipmentReviewed}
          />
        ) : null}
      </Section>

      <Section
        id="pricing"
        index={3}
        state={stateOf(`pricing`)}
        hint={hintFor(`pricing`)}
      >
        {stateOf(`pricing`) === `open` ? (
          <FinishPricing proposal={proposal} />
        ) : null}
      </Section>
    </div>
  )
}
